<template>
  <div class="image-face-editor">
    <ImageFaceOverlay
      :faces="frameReady ? faces : []"
      :selected-face-id="selectedFaceId"
      :loading="loading || !frameReady"
      @select="selectFace"
    />
    <div v-if="loadError" class="face-load-error" @click.stop @dblclick.stop>
      <span>{{ loadError }}</span>
      <b-button size="sm" variant="outline-light" @click="loadFaces">
        {{ $t('retry') }}
      </b-button>
    </div>
    <ImageFacePanel
      v-if="selectedFace"
      :class="{ 'on-left': selectedFace.x + selectedFace.width / 2 > .5 }"
      :face="selectedFace"
      :targets="targets"
      :busy="busy || targetsLoading"
      :error="operationError"
      @close="selectedFaceId = null"
      @assign="assignFace"
      @create-draft="createDraftFromFace"
      @create-unconfirmed="createUnconfirmedFromFace"
      @ignore="ignoreFace"
    />
  </div>
</template>

<script>
import People from '../../js/model/people.js'
import ImageFaceOverlay from './ImageFaceOverlay.vue'
import ImageFacePanel from './ImageFacePanel.vue'

export default {
  name: 'ImageFaceEditor',
  components: { ImageFaceOverlay, ImageFacePanel },
  props: {
    mediaId: { type: Number, required: true },
    frameReady: { type: Boolean, default: false },
  },
  data() {
    return {
      faces: [],
      targets: [],
      targetsLoaded: false,
      targetsLoading: false,
      targetsPromise: null,
      loading: false,
      busy: false,
      loadError: '',
      operationError: '',
      selectedFaceId: null,
      requestId: 0,
    }
  },
  computed: {
    selectedFace() {
      return this.faces.find(face => face.id === this.selectedFaceId) || null
    },
  },
  watch: {
    mediaId() {
      this.selectedFaceId = null
      this.operationError = ''
      this.loadFaces()
    },
  },
  created() {
    this.model = new People({ http: this.$http })
    this.loadFaces()
  },
  beforeDestroy() {
    this.requestId += 1
  },
  methods: {
    async loadFaces() {
      const mediaId = this.mediaId
      const requestId = ++this.requestId
      this.loading = true
      this.loadError = ''
      this.faces = []
      try {
        const response = await this.model.mediaFaces(mediaId)
        if (requestId !== this.requestId || mediaId !== this.mediaId) return
        this.faces = response.items
      } catch (error) {
        if (requestId !== this.requestId || mediaId !== this.mediaId) return
        this.loadError = this.errorMessage(error)
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    async selectFace(face) {
      if (this.busy) return
      this.selectedFaceId = face.id
      this.operationError = ''
      await this.loadTargets()
    },
    closeSelection() {
      if (this.selectedFaceId == null) return false
      this.selectedFaceId = null
      return true
    },
    async loadTargets() {
      if (this.targetsLoaded) return
      if (this.targetsPromise) return this.targetsPromise
      this.targetsLoading = true
      this.targetsPromise = this.model.targets()
        .then(response => {
          this.targets = response.items
          this.targetsLoaded = true
        })
        .catch(error => {
          this.operationError = this.errorMessage(error)
        })
        .finally(() => {
          this.targetsLoading = false
          this.targetsPromise = null
        })
      return this.targetsPromise
    },
    async assignFace(target) {
      await this.updateSelectedFace(
        face => {
          face.person_id = target.id
          face.person = target
          face.assignment_state = target.status === 'confirmed' ? 'admin_approved' : 'clustered'
        },
        face => this.model.move([face.id], target),
      )
    },
    async createDraftFromFace(name) {
      await this.updateSelectedFace(
        face => {
          face.person_id = null
          face.person = { id: null, name, status: 'candidate' }
          face.assignment_state = 'clustered'
        },
        async face => {
          const person = await this.model.createDraft([face.id], name)
          face.person_id = person.id
          face.person = person
          if (!this.targets.some(target => target.id === person.id)) {
            this.targets.push(person)
            this.targets.sort((first, second) => first.status.localeCompare(second.status)
              || first.name.localeCompare(second.name))
          }
        },
      )
    },
    async createUnconfirmedFromFace() {
      await this.updateSelectedFace(
        face => {
          face.person_id = null
          face.person = { id: null, name: '', status: 'candidate' }
          face.assignment_state = 'clustered'
        },
        async face => {
          const person = await this.model.createUnconfirmed([face.id])
          face.person_id = person.id
          face.person = person
        },
      )
    },
    async ignoreFace() {
      await this.updateSelectedFace(
        face => {
          face.person_id = null
          face.person = null
          face.assignment_state = 'ignored'
        },
        face => this.model.ignore([face.id]),
      )
    },
    async updateSelectedFace(optimisticUpdate, operation) {
      const face = this.selectedFace
      if (!face || this.busy) return
      const mediaId = this.mediaId
      const faceId = face.id
      const snapshot = { ...face, person: face.person ? { ...face.person } : null }
      this.busy = true
      this.operationError = ''
      optimisticUpdate(face)
      try {
        await operation(face)
      } catch (error) {
        Object.assign(face, snapshot)
        if (this.mediaId === mediaId && this.selectedFaceId === faceId) {
          this.operationError = this.errorMessage(error)
        }
      } finally {
        this.busy = false
      }
    },
    errorMessage(error) {
      return error.response?.data?.error || error.message || String(error)
    },
  },
}
</script>

<style scoped>
.image-face-editor {
  display: contents;
}

.face-load-error {
  position: fixed;
  top: 24px;
  left: 50%;
  z-index: 5;
  display: flex;
  gap: 12px;
  align-items: center;
  max-width: min(620px, calc(100vw - 240px));
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(116, 25, 25, .95);
  transform: translateX(-50%);
}
</style>
